/** biome-ignore-all lint/style/noNestedTernary: legacy code */
import { ImageSelectorTab } from '@amzn/journeys-common';
import { Box, Button, Heading, Row } from '@amzn/meridian';
import { ErrorAlert } from 'components/ErrorAlert';
import { cloneDeep, isEmpty, map } from 'es-toolkit/compat';
import { useProjectAssetQuery } from 'generated/graphql';
import { type Dispatch, type FC, useEffect, useMemo, useState } from 'react';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { ActiveFilters } from '../filters/ActiveFilters';
import { FilterSearchInput } from '../filters/FilterSearchInput';
import { type SummaryGroup, useAssetFilters } from '../filters/filters.util';
import { GroupCard } from '../filters/GroupCard';
import { SortSelector } from '../filters/SortSelector';
import { SummaryFilter } from '../filters/SummaryFilter';
import {
  assetGroupList,
  assetToGalleryImage,
  imageGalleryMaxHeight,
  imageSortOptions,
  useImageGallerySelection,
  useImageRenaming,
} from './assets.util';
import type { AssetSelectionExisting } from './assets.util.types';
import { ImageGallery } from './ImageGallery';
import type { ImageSelectorModalAction } from './image-selector-modal.actions';

export type ImagesFromProjectSelectorProps = {
  // Params that will be used to create filters inside of the image-selector-modal
  // starting off this will just be dimensions.
  filterParams: SummaryGroup[];
  visible: boolean;
  pageSize: number;
  multiselect: boolean;
  selectedAssets: AssetSelectionExisting[];
  dispatch: Dispatch<ImageSelectorModalAction>;
  projectId: string;
};

export const ImagesFromProjectSelector: FC<ImagesFromProjectSelectorProps> = ({
  visible,
  filterParams,
  pageSize,
  multiselect,
  selectedAssets,
  dispatch,
  projectId,
}) => {
  const { data, loading, error } = useProjectAssetQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        maxHeight: imageGalleryMaxHeight,
        projectId,
      },
    },
  });

  const { selectedAssetIds, images, onSelectionChanged } = useImageGallerySelection({
    assetSearchResults: data?.projectAssets ?? [],
    dispatch,
    from: ImageSelectorTab.thisProject,
    selectedAssets,
  });

  const { onImageNameChanged } = useImageRenaming();

  const {
    sortAsc,
    updateSortSelection,
    toggleSortDir,
    addSearchQuery,
    sortBy,
    groupBy,
    groups,
    possibleFilterings,
    clearGrouping,
    filters,
    clearAll,
    removeFilter,
    setGroupBy,
    addGroupFilter,
    isNilGroup,
    orderedAssets,
    setFilterGroups,
  } = useAssetFilters({
    images,
  });

  const hasImages = !isEmpty(images);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    setFilterGroups(cloneDeep(filterParams));
  }, [
    filterParams,
    setFilterGroups,
    visible,
  ]);

  const [numShowing, setNumShowing] = useState(pageSize);
  const onShowMoreResults = () => {
    setNumShowing(numShowing + pageSize);
  };

  const orderedImages = useMemo(
    () => map(orderedAssets, assetToGalleryImage).slice(0, numShowing),
    [
      numShowing,
      orderedAssets,
    ],
  );
  const hasMoreResults = numShowing < orderedAssets.length;

  return (
    <>
      <Row alignmentVertical="center" spacing="300">
        <Heading data-cy="assets-in-this-project" level={2} type="h200">
          Assets in this project
        </Heading>
        <ActiveFilters filters={filters} onClearAll={clearAll} onRemove={removeFilter} />
      </Row>
      <Row alignmentVertical="bottom" spacing="400" spacingInset="100" wrap="down">
        <SummaryFilter
          groupedBy={groupBy}
          groupList={assetGroupList}
          onClear={clearGrouping}
          possibleGroupingsMap={possibleFilterings}
          setGroupBy={setGroupBy}
        />
        <Row alignmentVertical="bottom" wrap="down">
          <FilterSearchInput addSearchQuery={addSearchQuery} id="asset-summary-search-input" />
          <SortSelector
            groupBy={groupBy}
            onToggleSortDir={toggleSortDir}
            onUpdateSortSelection={updateSortSelection}
            sortAsc={sortAsc}
            sortBy={sortBy}
            sortOptions={imageSortOptions}
          />
        </Row>
      </Row>
      {groupBy ? (
        <div className="mr-4 group-card-container pt-4">
          {groups.map((group) => (
            <GroupCard
              cardType="assets"
              groupType={group.groupType}
              isNilGroup={isNilGroup(group)}
              key={group.name}
              name={group.name}
              numItems={group.numItems}
              onClick={() => addGroupFilter(group)}
            />
          ))}
        </div>
      ) : hasImages || loading || error || hasMoreResults ? (
        <Box spacingInset="300 none none">
          {orderedImages.length > 0 || loading ? (
            <div className="image-gallery-container">
              <ImageGallery
                images={orderedImages}
                multiselect={multiselect}
                onImageNameChanged={onImageNameChanged}
                onSelectionChanged={onSelectionChanged}
                selectable
                selectedAssets={selectedAssetIds}
                visible={visible}
              />
              {hasMoreResults && (
                <Row alignmentHorizontal="center" spacingInset="300 none none">
                  <Button onClick={onShowMoreResults} size="small" type="secondary">
                    {`Show ${pageSize} more results`}
                  </Button>
                </Row>
              )}
            </div>
          ) : error ? (
            <ErrorAlert contextMessage="Unable to load project images" error={error} />
          ) : (
            <div>No images found for the current filters.</div>
          )}
          <LoadingOverlay visible={loading} />
        </Box>
      ) : (
        <div className="pt-3">
          <p>
            There are no images in the current project. Click <strong>From your file system </strong>
            to upload images from your files system, <strong>From other projects</strong> to search for images contained in other projects,
            or <strong>From URL</strong> to insert an image from a URL.
          </p>
        </div>
      )}
    </>
  );
};
